import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import {
  applyFilters,
  buildCarTypeFilterIndex,
  getDepositBucket,
  DEPOSIT_BUCKETS,
  getTerminalLabel,
} from "../utils/resultsPipeline";

const CAR_TYPE_ORDER = ["Cars", "SUVs", "Vans & Trucks", "Others"];

const CAR_CHILD_ORDER = [
  "Mini", "Mini Elite",
  "Economy", "Economy Elite",
  "Compact", "Compact Elite", "Mid-Size",
  "Intermediate", "Intermediate Elite",
  "Standard", "Standard Elite",
  "Full Size", "Full Size Elite",
  "Premium", "Premium Elite",
  "Luxury", "Luxury Elite",
  "Oversize", "Special",
];

const SUV_CHILD_ORDER = [
  "Economy SUV", "Economy Elite SUV",
  "Compact SUV", "Compact Elite SUV", "Mid-Size SUV",
  "Intermediate SUV", "Intermediate Elite SUV",
  "Standard SUV", "Standard Elite SUV",
  "Full Size SUV", "Full Size Elite SUV",
  "Premium SUV", "Premium Elite SUV",
  "Luxury SUV", "Luxury Elite SUV",
  "Mini SUV", "Mini Elite SUV",
  "Oversize SUV", "Special SUV",
];

const VAN_CHILD_ORDER = [
  "Passenger Van", "Minivan", "Cargo Van", "Pickup Truck",
];

const OTHERS_CHILD_ORDER = [
  "Convertible", "Special", "Roadster",
  "Recreational Vehicle", "Motor Home",
];

const CHILD_ORDER_MAP = {
  "Cars": CAR_CHILD_ORDER,
  "SUVs": SUV_CHILD_ORDER,
  "Vans & Trucks": VAN_CHILD_ORDER,
  "Others": OTHERS_CHILD_ORDER,
};

const FilterMenu = ({
  carRates = [],
  selectedFilters = {},
  onFilterChange,
  isMobileOpen,
  setIsMobileOpen,
}) => {
  const [expandedCategories, setExpandedCategories] = useState({
    "Car Type": true,
    Transmission: true,
    Supplier: true,
    Miles: true,
    Deposit: true,
    "Terminal Type": true,
  });

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleCheckboxChange = (category, value) => {
    const currentValues = selectedFilters[category] || [];
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];

    const nextFilters = { ...selectedFilters };
    if (nextValues.length === 0) delete nextFilters[category];
    else nextFilters[category] = nextValues;

    onFilterChange?.(nextFilters);
  };

  const handleCarTypeChange = (parent, child = null) => {
    const current = selectedFilters["Car Type"] || [];

    // Match exactly what gets rendered
    const data = totalOptions["Car Type"]?.[parent];
    const childrenOfParent = (CHILD_ORDER_MAP[parent] || Object.keys(data?.sub || {}))
      .filter((c) => data?.sub?.[c] > 0);

    let next;

    if (child === null) {
      const parentChecked = current.includes(parent);
      if (parentChecked) {
        next = current.filter((v) => v !== parent && !v.startsWith(`${parent}:`));
      } else {
        const childKeys = childrenOfParent.map((c) => `${parent}:${c}`);
        next = [...new Set([...current, parent, ...childKeys])];
      }
    } else {
      const childKey = `${parent}:${child}`;
      const childChecked = current.includes(childKey);

      if (childChecked) {
        next = current.filter((v) => v !== childKey && v !== parent);
      } else {
        next = [...current, childKey];
        const allChildrenChecked = childrenOfParent.every((c) =>
          next.includes(`${parent}:${c}`)
        );
        if (allChildrenChecked) next = [...next, parent];
      }
    }

    const nextFilters = { ...selectedFilters };
    if (next.length === 0) delete nextFilters["Car Type"];
    else nextFilters["Car Type"] = next;
    onFilterChange?.(nextFilters);
  };

  const getParentState = (parent) => {
    const current = selectedFilters["Car Type"] || [];
    const data = totalOptions["Car Type"]?.[parent];
    const children = (CHILD_ORDER_MAP[parent] || Object.keys(data?.sub || {}))
      .filter((c) => data?.sub?.[c] > 0);

    if (children.length === 0) return "unchecked";
    const checkedChildren = children.filter((c) => current.includes(`${parent}:${c}`));
    if (current.includes(parent)) return "checked";
    if (checkedChildren.length > 0) return "indeterminate";
    return "unchecked";
  };

  const clearAll = () => onFilterChange?.({});

  const withoutFacet = (filters, facet) => {
    const next = { ...(filters || {}) };
    delete next[facet];
    return next;
  };

  const totalOptions = useMemo(() => {
    const options = {
      "Car Type": buildCarTypeFilterIndex(carRates),
      Transmission: {},
      Supplier: {},
      Miles: {},
      Deposit: {},
      "Terminal Type": {},
    };

    carRates.forEach((v) => {
      if (v.transmission)
        options.Transmission[v.transmission] = (options.Transmission[v.transmission] || 0) + 1;

      const supplierKey = v.name || "Unknown";
      if (supplierKey)
        options.Supplier[supplierKey] = (options.Supplier[supplierKey] || 0) + 1;

      const m = v.primaryOffer?.mileageUnlimited;
      if (m === true || m === false) {
        const label = m ? "Unlimited" : "Limited";
        options.Miles[label] = (options.Miles[label] || 0) + 1;
      }

      const bucket = getDepositBucket(v.primaryOffer?.depositAmount);
      if (bucket) options.Deposit[bucket] = (options.Deposit[bucket] || 0) + 1;

      const termLabel = getTerminalLabel(v.pickupType);
      options["Terminal Type"][termLabel] = (options["Terminal Type"][termLabel] || 0) + 1;
    });

    return options;
  }, [carRates]);

  useEffect(() => {
    const isValid = {
      "Car Type": (v) => {
        const [parent, child] = v.split(":");
        const data = totalOptions["Car Type"]?.[parent];
        if (!data) return false;
        return child ? Boolean(data.sub?.[child]) : true;
      },
      Transmission: (v) => Boolean(totalOptions.Transmission[v]),
      Supplier: (v) => Boolean(totalOptions.Supplier[v]),
      Miles: (v) => Boolean(totalOptions.Miles[v]),
      Deposit: (v) => Boolean(totalOptions.Deposit[v]),
      "Terminal Type": (v) => Boolean(totalOptions["Terminal Type"][v]),
    };

    const pruned = {};
    let changed = false;

    Object.entries(selectedFilters).forEach(([facet, values]) => {
      const list = values || [];
      const keep = list.filter((v) => isValid[facet]?.(v));
      if (keep.length !== values.length) changed = true;
      if (keep.length) pruned[facet] = keep;
    });

    if (changed) onFilterChange?.(pruned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalOptions]);

  const availableOptions = useMemo(() => {
    const facets = ["Car Type", "Transmission", "Supplier", "Miles", "Deposit", "Terminal Type"];
    const result = {};

    facets.forEach((facet) => {
      const filtered = applyFilters(carRates, withoutFacet(selectedFilters, facet));
      const options = {
        "Car Type": buildCarTypeFilterIndex(filtered),
        Transmission: {},
        Supplier: {},
        Miles: {},
        Deposit: {},
        "Terminal Type": {},
      };

      filtered.forEach((v) => {
        if (v.transmission)
          options.Transmission[v.transmission] = (options.Transmission[v.transmission] || 0) + 1;

        const supplierKey = v.name || "Unknown";
        options.Supplier[supplierKey] = (options.Supplier[supplierKey] || 0) + 1;

        const m = v.primaryOffer?.mileageUnlimited;
        if (m === true || m === false) {
          const label = m ? "Unlimited" : "Limited";
          options.Miles[label] = (options.Miles[label] || 0) + 1;
        }

        const bucket = getDepositBucket(v.primaryOffer?.depositAmount);
        if (bucket) options.Deposit[bucket] = (options.Deposit[bucket] || 0) + 1;

        const termLabel = getTerminalLabel(v.pickupType);
        options["Terminal Type"][termLabel] = (options["Terminal Type"][termLabel] || 0) + 1;
      });

      result[facet] = options[facet];
    });

    return result;
  }, [carRates, selectedFilters]);

  const FilterContent = () => (
    <>
      {/* Header */}
      <div className="px-5 py-4 border-b border-surface-border bg-surface sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-heading font-bold text-text uppercase tracking-wider">
            Filters
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={clearAll}
              className="text-sm font-semibold text-primary hover:text-primary-hover transition-colors"
            >
              Reset all
            </button>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="lg:hidden text-text-muted hover:text-text transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="divide-y divide-surface-border overflow-y-auto max-h-[75vh] lg:max-h-none px-5">

        {/* Car Type */}
        <FilterSection
          title="Car Type"
          isOpen={expandedCategories["Car Type"]}
          onToggle={() => toggleCategory("Car Type")}
        >
          {CAR_TYPE_ORDER
            .filter((parent) => totalOptions["Car Type"][parent])
            .map((parent) => {
              const data = totalOptions["Car Type"][parent];
              const availableParent = availableOptions["Car Type"]?.[parent]?.count || 0;
              const parentChecked = selectedFilters["Car Type"]?.includes(parent);
              const parentDisabled = !parentChecked && availableParent === 0;

              return (
                <div key={parent}>
                  <FilterRow
                    label={parent}
                    count={availableParent}
                    checked={parentChecked}
                    indeterminate={getParentState(parent) === "indeterminate"}
                    disabled={parentDisabled}
                    onChange={() => handleCarTypeChange(parent)}
                  />
                  {Object.keys(data.sub).length > 0 && (
                    <div className="ml-5 mt-2 space-y-2 pl-3">
                      {(CHILD_ORDER_MAP[parent] || Object.keys(data.sub))
                        .filter((child) => data.sub[child])
                        .map((child) => {
                          const count = data.sub[child];
                          const availableChild = availableOptions["Car Type"]?.[parent]?.sub?.[child] || 0;
                          const childChecked = selectedFilters["Car Type"]?.includes(`${parent}:${child}`);
                          const childDisabled = !childChecked && availableChild === 0;
                          return (
                            <FilterRow
                              key={child}
                              label={child}
                              count={availableChild}
                              checked={childChecked}
                              disabled={childDisabled}
                              onChange={() => handleCarTypeChange(parent, child)}
                            />
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
        </FilterSection>

        {/* Transmission */}
        <FilterSection
          title="Transmission"
          isOpen={expandedCategories.Transmission}
          onToggle={() => toggleCategory("Transmission")}
        >
          {Object.entries(totalOptions.Transmission).map(([type, count]) => (
            <FilterRow
              key={type}
              label={type}
              count={availableOptions.Transmission?.[type] || 0}
              checked={selectedFilters.Transmission?.includes(type)}
              disabled={!selectedFilters.Transmission?.includes(type) && (availableOptions.Transmission?.[type] || 0) === 0}
              onChange={() => handleCheckboxChange("Transmission", type)}
            />
          ))}
        </FilterSection>

        {/* Supplier */}
    {/* Supplier */}
<FilterSection
  title="Supplier"
  isOpen={expandedCategories.Supplier}
  onToggle={() => toggleCategory("Supplier")}
>
  {Object.entries(totalOptions.Supplier)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, count]) => (
      <FilterRow
        key={name}
        label={name}
        count={availableOptions.Supplier?.[name] || 0}
        checked={selectedFilters.Supplier?.includes(name)}
        disabled={!selectedFilters.Supplier?.includes(name) && (availableOptions.Supplier?.[name] || 0) === 0}
        onChange={() => handleCheckboxChange("Supplier", name)}
      />
    ))}
</FilterSection>
        {/* Deposit */}
        <FilterSection
          title="Deposit"
          isOpen={expandedCategories.Deposit}
          onToggle={() => toggleCategory("Deposit")}
        >
          {DEPOSIT_BUCKETS.filter((bucket) => totalOptions.Deposit[bucket]).map((bucket) => {
            const count = availableOptions.Deposit?.[bucket] || 0;
            const checked = selectedFilters.Deposit?.includes(bucket);
            const currency = carRates[0]?.primaryOffer?.currency || "";
            return (
              <FilterRow
                key={bucket}
                label={bucket === "Zero Deposit" ? bucket : `${bucket} ${currency}`}
                count={count}
                checked={checked}
                disabled={!checked && (availableOptions.Deposit?.[bucket] || 0) === 0}
                onChange={() => handleCheckboxChange("Deposit", bucket)}
              />
            );
          })}
        </FilterSection>

        {/* Miles */}
        <FilterSection
          title="Miles"
          isOpen={expandedCategories.Miles}
          onToggle={() => toggleCategory("Miles")}
        >
          {Object.entries(totalOptions.Miles).map(([type, count]) => (
            <FilterRow
              key={type}
              label={type}
              count={availableOptions.Miles?.[type] || 0}
              checked={selectedFilters.Miles?.includes(type)}
              disabled={!selectedFilters.Miles?.includes(type) && (availableOptions.Miles?.[type] || 0) === 0}
              onChange={() => handleCheckboxChange("Miles", type)}
            />
          ))}
        </FilterSection>

        {/* Terminal Type */}
        <FilterSection
          title="Terminal Type"
          isOpen={expandedCategories["Terminal Type"]}
          onToggle={() => toggleCategory("Terminal Type")}
        >
          {Object.entries(totalOptions["Terminal Type"]).map(([type, count]) => (
            <FilterRow
              key={type}
              label={type}
              count={availableOptions["Terminal Type"]?.[type] || 0}
              checked={selectedFilters["Terminal Type"]?.includes(type)}
              disabled={!selectedFilters["Terminal Type"]?.includes(type) && (availableOptions["Terminal Type"]?.[type] || 0) === 0}
              onChange={() => handleCheckboxChange("Terminal Type", type)}
            />
          ))}
        </FilterSection>

      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block bg-surface rounded-xl border border-surface-border shadow-card overflow-hidden">
        <FilterContent />
      </aside>

      {/* Mobile Drawer */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[999] flex flex-col bg-surface animate-in slide-in-from-bottom duration-300">
          <FilterContent />
          <div className="p-4 border-t border-surface-border bg-surface mt-auto">
            <button
              onClick={() => setIsMobileOpen(false)}
              className="w-full bg-primary hover:bg-primary-hover text-white py-4 rounded-xl font-heading font-bold uppercase tracking-wider text-sm transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const FilterSection = ({ title, isOpen, onToggle, children }) => {
  const hasContent = React.Children.toArray(children).some(Boolean);
  if (!hasContent) return null;
  return (
  <div>
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between py-4 text-left"
    >
      <span className="text-xs font-heading font-bold text-text uppercase tracking-wider">
        {title}
      </span>
      {isOpen
        ? <ChevronUp className="w-4 h-4 text-text-muted" />
        : <ChevronDown className="w-4 h-4 text-text-muted" />
      }
    </button>
    {isOpen && (
      <div className="pb-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
        {children}
      </div>
    )}
  </div>
)};

const FilterRow = ({ label, count, checked, indeterminate, disabled, onChange, isSmall = false }) => (
  <label className={`flex items-center justify-between group cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
    <div className="flex items-center gap-3">
      <div className={`
        rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0
        ${isSmall ? "w-4 h-4" : "w-5 h-5"}
        ${checked
          ? "bg-primary border-primary"
          : indeterminate
            ? "bg-accent border-primary"
            : "border-surface-border bg-surface group-hover:border-primary"
        }
      `}>
        {checked && (
          <Check className={`${isSmall ? "w-2.5 h-2.5" : "w-3 h-3"} text-white stroke-[3]`} />
        )}
        {indeterminate && !checked && (
          <div className={`${isSmall ? "w-1.5 h-0.5" : "w-2 h-0.5"} bg-primary rounded-full`} />
        )}
      </div>
      <span className={`
        font-body text-sm transition-colors
        ${checked || indeterminate
          ? "font-semibold text-text"
          : "font-normal text-text group-hover:text-text"
        }
        ${disabled && !checked && !indeterminate ? "line-through" : ""}
      `}>
        {label}
      </span>
    </div>
    <span className="text-sm text-text-muted flex-shrink-0 ml-2">{count}</span>
    <input type="checkbox" className="hidden" checked={checked || false} disabled={disabled} onChange={onChange} />
  </label>
);

export default FilterMenu;